import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router";
import { useGlobalStore } from "~/stores/store-provider";
import {
  FaArrowLeft,
  FaCheck,
  FaTimes,
  FaKey,
  FaInfoCircle,
} from "react-icons/fa";
import { SiShopify } from "react-icons/si";
import { HiOutlineGlobe } from "react-icons/hi";

type ShopifyFormData = {
  name: string;
  shopName: string;
  accessToken: string;
  webhookVersion: string;
};

type FormErrors = {
  [key in keyof ShopifyFormData]?: string;
};

const ShopifyConnection = () => {
  const navigate = useNavigate();
  const { addConnection } = useGlobalStore((state) => state);
  const timersRef = useRef<Set<NodeJS.Timeout>>(new Set());

  const [formData, setFormData] = useState<ShopifyFormData>({
    name: "",
    shopName: "",
    accessToken: "",
    webhookVersion: "2025-07",
  });

  const [errors, setErrors] = useState<FormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const [toastType, setToastType] = useState<"success" | "error">("success");

  // Cleanup timers on unmount
  useEffect(() => {
    return () => {
      timersRef.current.forEach(timer => clearTimeout(timer));
    };
  }, []);

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Clear error for this field
    if (errors[name as keyof FormErrors]) {
      setErrors((prev) => ({ ...prev, [name]: undefined }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    // Validate connection name
    if (!formData.name.trim()) {
      newErrors.name = "Connection name is required";
    } else if (formData.name.length < 3 || formData.name.length > 50) {
      newErrors.name = "Must be between 3 and 50 characters";
    }

    // Validate shop name
    if (!formData.shopName.trim()) {
      newErrors.shopName = "Shop name is required";
    } else if (!formData.shopName.match(/^[a-zA-Z0-9-]+$/)) {
      newErrors.shopName = "Only letters, numbers, and hyphens allowed";
    }

    // Validate access token
    if (!formData.accessToken.trim()) {
      newErrors.accessToken = "Access token is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      const connectionData = {
        connection_name: formData.name,
        shop_name: formData.shopName,
        access_token: formData.accessToken,
        webhook_version: formData.webhookVersion,
        type: "shopify",
        connection_type: "shopify",
      };

      await addConnection(connectionData);

      // Show success toast before navigating
      setToastType("success");
      setToastMessage("Store connected successfully!");
      setShowToast(true);

      const timer = setTimeout(() => {
        navigate("/connections");
        timersRef.current.delete(timer);
      }, 1500);
      timersRef.current.add(timer);
    } catch (error) {
      console.error("Failed to save connection:", error);

      // Show error toast
      setToastType("error");
      setToastMessage("Failed to save connection. Please try again.");
      setShowToast(true);

      const timer = setTimeout(() => {
        setShowToast(false);
        timersRef.current.delete(timer);
      }, 2000);
      timersRef.current.add(timer);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="h-full bg-base-200/50 overflow-y-auto">
      <div className="container mx-auto px-4 py-8 max-w-2xl min-h-full">
        {/* Back Navigation */}
        <button
          onClick={() => navigate("/connections")}
          className="btn btn-ghost btn-sm gap-2 mb-6"
        >
          <FaArrowLeft className="w-3 h-3" />
          Back to Connections
        </button>

        {/* Main Card */}
        <div className="card bg-base-100 shadow-lg">
          <div className="card-body p-8">
            {/* Header */}
            <div className="flex items-center gap-4 mb-8 pb-6 border-b border-base-200">
              <div className="p-3 bg-green-500/10 rounded-lg">
                <SiShopify className="w-7 h-7 text-green-600" />
              </div>
              <div>
                <h1 className="text-xl font-bold">Connect Shopify Store</h1>
                <p className="text-sm text-base-content/60">
                  Sync your products, orders, and customer data
                </p>
              </div>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Connection Name */}
              <div className="form-control w-full">
                <label className="label mb-2">
                  <span className="label-text font-medium">
                    Connection Name
                  </span>
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  placeholder="e.g., Production Store"
                  className={`input input-bordered w-full ${
                    errors.name ? "input-error" : ""
                  }`}
                />
                {errors.name && (
                  <label className="label">
                    <span className="label-text-alt text-error">
                      {errors.name}
                    </span>
                  </label>
                )}
              </div>

              {/* Shop URL */}
              <div className="form-control w-full">
                <label className="label mb-2">
                  <span className="label-text font-medium">Shop URL</span>
                  <div
                    className="tooltip"
                    data-tip="Your Shopify store subdomain"
                  >
                    <FaInfoCircle className="w-3 h-3 text-base-content/50" />
                  </div>
                </label>
                <div className="join w-full">
                  <span className="join-item input input-bordered bg-base-200 flex items-center px-3">
                    <HiOutlineGlobe className="w-4 h-4 text-base-content/60" />
                  </span>
                  <input
                    type="text"
                    name="shopName"
                    value={formData.shopName}
                    onChange={handleInputChange}
                    placeholder="your-shop-name"
                    className={`join-item input input-bordered flex-1 ${
                      errors.shopName ? "input-error" : ""
                    }`}
                  />
                  <span className="join-item input input-bordered bg-base-200 text-base-content/60">
                    .myshopify.com
                  </span>
                </div>
                {errors.shopName && (
                  <label className="label">
                    <span className="label-text-alt text-error">
                      {errors.shopName}
                    </span>
                  </label>
                )}
              </div>

              {/* Access Token */}
              <div className="form-control w-full">
                <label className="label mb-2">
                  <span className="label-text font-medium">
                    Admin API Access Token
                  </span>
                  <div
                    className="tooltip"
                    data-tip="Generate from Shopify Admin → Settings → Apps → Develop apps"
                  >
                    <FaInfoCircle className="w-3 h-3 text-base-content/50" />
                  </div>
                </label>
                <label className="input input-bordered flex items-center gap-2 w-full">
                  <FaKey className="w-4 h-4 text-base-content/50" />
                  <input
                    type="password"
                    name="accessToken"
                    value={formData.accessToken}
                    onChange={handleInputChange}
                    placeholder="shpat_xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
                    className="grow font-mono text-sm"
                  />
                </label>
                {errors.accessToken && (
                  <label className="label">
                    <span className="label-text-alt text-error">
                      {errors.accessToken}
                    </span>
                  </label>
                )}
              </div>

              {/* API Version */}
              <div className="form-control w-full">
                <label className="label mb-2">
                  <span className="label-text font-medium">API Version</span>
                </label>
                <select
                  name="webhookVersion"
                  value={formData.webhookVersion}
                  onChange={handleInputChange}
                  className="select select-bordered w-full"
                >
                  <option value="2025-07">2025-07 (Recommended)</option>
                  <option value="2025-04">2025-04</option>
                  <option value="2025-01">2025-01</option>
                  <option value="2024-10">2024-10</option>
                </select>
              </div>

              {/* Info Alert */}
              <div className="alert bg-base-200/50 border-0">
                <FaInfoCircle className="w-4 h-4" />
                <div className="text-sm">
                  <p className="font-medium">What happens next?</p>
                  <p className="text-base-content/70">
                    We'll sync your store data and set up webhooks for real-time
                    updates. The initial sync may take a few minutes.
                  </p>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex justify-end gap-3 pt-4 border-t border-base-200">
                <button
                  type="button"
                  onClick={() => navigate("/connections")}
                  className="btn btn-ghost"
                  disabled={isSubmitting}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={
                    isSubmitting ||
                    !formData.name.trim() ||
                    !formData.shopName.trim() ||
                    !formData.accessToken.trim()
                  }
                  className="btn btn-primary"
                >
                  {isSubmitting ? (
                    <>
                      <span className="loading loading-spinner loading-sm"></span>
                      Connecting...
                    </>
                  ) : (
                    "Save Connection"
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>

        {/* Help Link */}
        <div className="text-center mt-6">
          <a
            href="https://shopify.dev/docs/apps/auth/admin-app-access-tokens"
            target="_blank"
            rel="noopener noreferrer"
            className="link link-primary text-sm"
          >
            Need help? View Shopify API documentation →
          </a>
        </div>
      </div>

      {/* Toast Notification */}
      {showToast && (
        <div className="toast toast-bottom toast-center">
          <div className={`alert ${toastType === 'success' ? 'alert-success' : 'alert-error'}`}>
            {toastType === 'success' ? (
              <FaCheck className="w-4 h-4" />
            ) : (
              <FaTimes className="w-4 h-4" />
            )}
            <span>{toastMessage}</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default ShopifyConnection;
